'use client';

import { useState, useEffect, useCallback } from 'react';
import { LocalStorage } from '@/src/utils/general/local-storage';
import { useRealTimeInterval } from '@/src/hooks/use-realtime-interval';
import type { RealTimeConfig } from '../types';

// LocalStorage key per DASHBOARD_ARCHITECTURE.md
const STORAGE_KEY = 'dashboard-realtime-config';

// Default config
const defaultConfig: RealTimeConfig = {
  isActive: false,
  requestIntervalMinutes: 5, // 5 minutes
  lastUpdatedAt: undefined,
};

/**
 * Initialize from localStorage or use defaults
 */
function getInitialConfig(): RealTimeConfig {
  const stored = LocalStorage.get(STORAGE_KEY);
  if (stored && typeof stored === 'object') {
    return {
      ...defaultConfig,
      ...stored,
      // Ensure interval is in minutes (legacy stored values might be in ms)
      requestIntervalMinutes: stored.requestIntervalMinutes > 100
        ? Math.round(stored.requestIntervalMinutes / 60000) // Convert from ms to minutes
        : stored.requestIntervalMinutes,
    };
  }
  return defaultConfig;
}

/**
 * useDashboardRealTime - Manages real-time polling configuration
 *
 * Extracted from dashboard-overview.tsx to enable shared state across
 * dashboard sub-routes via context.
 *
 * Features:
 * - Configurable polling intervals (5 or 10 minutes)
 * - Auto-disables when date range is not today
 * - Triggers filter reload on interval
 * - Persists configuration to localStorage
 *
 * @param isDateRangeToday - Whether the current date filter is today
 * @param triggerReload - Callback to trigger data reload (updates filter.reloadTimestamp)
 */
export function useDashboardRealTime(
  isDateRangeToday: boolean,
  triggerReload: () => void
) {
  const [config, setConfigState] = useState<RealTimeConfig>(getInitialConfig);

  // Wrapper for setConfig that also updates lastUpdatedAt when activating
  const setConfig = useCallback((newConfig: RealTimeConfig | ((prev: RealTimeConfig) => RealTimeConfig)) => {
    setConfigState(prev => {
      const updated = typeof newConfig === 'function' ? newConfig(prev) : newConfig;
      // Save synchronously so page refresh immediately after a change doesn't lose the value
      LocalStorage.set(STORAGE_KEY, updated);
      return updated;
    });
  }, []);

  // Toggle real-time on/off
  const toggleRealTime = useCallback(() => {
    setConfig(prev => ({
      ...prev,
      isActive: !prev.isActive,
      lastUpdatedAt: !prev.isActive ? Date.now() : prev.lastUpdatedAt,
    }));
  }, [setConfig]);

  // Set interval (5 or 10 minutes, or null to disable)
  const setInterval = useCallback((minutes: number | null) => {
    setConfig(prev => ({
      ...prev,
      requestIntervalMinutes: minutes,
    }));
  }, [setConfig]);

  // Disable Real Time when the applied date range moves away from today.
  // Only reacts to isDateRangeToday changes -- NOT config.isActive changes.
  // This prevents the race condition where clicking "Real Time" in the date
  // picker sets isActive=true immediately, but the date change is deferred
  // until Apply. Without this fix the effect would see the stale
  // isDateRangeToday=false and kill isActive before Apply fires.
  useEffect(() => {
    if (!isDateRangeToday) {
      setConfig(prev => {
        if (!prev.isActive) return prev;
        return { ...prev, isActive: false };
      });
    }
  }, [isDateRangeToday, setConfig]);

  // Save config to localStorage whenever it changes
  useEffect(() => {
    LocalStorage.set(STORAGE_KEY, config);
  }, [config]);

  useRealTimeInterval(
    config.isActive,
    config.requestIntervalMinutes,
    () => {
      triggerReload();
      setConfig(prev => ({
        ...prev,
        lastUpdatedAt: Date.now(),
      }));
    },
    isDateRangeToday
  );

  return {
    config,
    setConfig,
    toggleRealTime,
    setInterval,
    // Convenience getters
    isActive: config.isActive,
    requestIntervalMinutes: config.requestIntervalMinutes,
    lastUpdatedAt: config.lastUpdatedAt,
    // Whether real-time can be enabled (only when viewing today's data)
    canEnableRealTime: isDateRangeToday,
  };
}

export type UseDashboardRealTimeReturn = ReturnType<typeof useDashboardRealTime>;
